import React from 'react';
import useGameStore from '../store/gameStore';
import { getMetiers, getSecteur, computeEquipeEffects, CONTRATS_SPECIAUX, OPTIONS_PLANNING, FORMATION, CHARGES_PATRONALES } from '../utils/retail';
import { formatMoney } from '../utils/format';
import { Users, GraduationCap, UserMinus, Sun, Moon, Trophy } from 'lucide-react';
import { toast } from '../components/ui/sonner';

/**
 * SCD Lot 2 — Écran « Équipe magasin » : recrutement par métier retail,
 * alternants, renforts saisonniers, formation, options de planning
 * (dimanche, nocturnes, primes sur objectifs).
 */
export default function Equipe() {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays);
  const treasury = useGameStore(s => s.treasury);
  const embaucherEmploye = useGameStore(s => s.embaucherEmploye);
  const licencierEmploye = useGameStore(s => s.licencierEmploye);
  const formerEmploye = useGameStore(s => s.formerEmploye);
  const toggleOptionPlanning = useGameStore(s => s.toggleOptionPlanning);

  if (!retail) {
    return <div className="text-slate-400 text-sm" data-testid="equipe-empty">Aucun magasin actif.</div>;
  }
  const sec = getSecteur(retail.secteur);
  const metiers = getMetiers(retail.secteur);
  const employes = (retail.equipe?.employes || []).filter(e => !e.finJour || e.finJour > totalDays);
  const options = retail.equipe?.options || {};
  const effects = computeEquipeEffects(retail, totalDays);

  const handleEmbauche = (metierId, contrat = 'cdi') => {
    const res = embaucherEmploye({ metierId, contrat });
    if (res?.ok) {
      toast('Embauche confirmée', { description: `${res.employe.nom} rejoint l'équipe (${formatMoney(Math.round(res.employe.salaire * CHARGES_PATRONALES))}/mois chargé).` });
    } else if (res?.reason === 'max_poste' || res?.reason === 'max_alternants') {
      toast.error('Effectif maximum atteint pour ce poste');
    }
  };

  const compteur = (metierId) => employes.filter(e => e.metierId === metierId && e.contrat === 'cdi').length;

  return (
    <div className="space-y-5" data-testid="equipe-page">
      <div>
        <p className="overline">{sec.nom} · {employes.length} employé(s) · Masse salariale {formatMoney(effects.masseSalarialeMensuelle)}/mois</p>
        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
          <Users className="w-6 h-6 text-accent" /> Équipe magasin
        </h1>
        {effects.sansDirecteur && (
          <p className="text-xs text-amber-400 mt-1" data-testid="alerte-directeur">⚠ Plus de 5 employés sans directeur de magasin : −5 % de conversion (encadrement insuffisant).</p>
        )}
      </div>

      {/* Options planning */}
      <div className="surface-card p-4" data-testid="options-planning">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3">Plannings & organisation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: 'dimanche', icon: Sun },
            { key: 'nocturnes', icon: Moon },
            { key: 'primes', icon: Trophy },
          ].map(({ key, icon: Icon }) => {
            const opt = OPTIONS_PLANNING[key];
            const active = !!options[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleOptionPlanning(key)}
                className={`text-left rounded-lg border p-3 transition-all ${active ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
                data-testid={`option-${key}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-accent" />
                  <span className="font-bold text-sm text-slate-100">{opt.nom}</span>
                  <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded ${active ? 'bg-emerald-600/20 text-emerald-400' : 'bg-ink-700 text-slate-500'}`}>{active ? 'Actif' : 'Inactif'}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{opt.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recrutement */}
      <div className="surface-card p-4" data-testid="recrutement">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3">Recruter (CDI)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {metiers.map(m => {
            const n = compteur(m.id);
            const plein = n >= (m.max || 9);
            return (
              <div key={m.id} className="rounded-lg border border-ink-600 bg-ink-900/40 p-3 flex flex-col" data-testid={`metier-${m.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{m.emoji}</span>
                  <span className="font-bold text-sm text-slate-100">{m.nom}</span>
                  <span className="ml-auto text-[10px] font-mono text-slate-500">{n}/{m.max}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug flex-1">{m.effet}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] font-mono text-slate-300">{formatMoney(Math.round(m.salaire * CHARGES_PATRONALES))}/mois</span>
                  <button
                    type="button"
                    disabled={plein}
                    onClick={() => handleEmbauche(m.id)}
                    className="bg-accent text-white px-3 py-1.5 rounded text-xs font-bold disabled:opacity-40 hover:bg-accent-hover transition-colors"
                    data-testid={`embaucher-${m.id}`}
                  >
                    Embaucher
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            type="button"
            onClick={() => handleEmbauche(null, 'alternance')}
            className="border border-ink-500 text-slate-200 px-4 py-2 rounded-lg text-xs font-bold hover:border-accent transition-colors"
            data-testid="embaucher-alternant"
          >
            + Alternant vendeur ({formatMoney(Math.round(CONTRATS_SPECIAUX.alternant.salaire * CHARGES_PATRONALES))}/mois · efficacité 50 %)
          </button>
          <button
            type="button"
            onClick={() => handleEmbauche(null, 'cdd_saisonnier')}
            className="border border-ink-500 text-slate-200 px-4 py-2 rounded-lg text-xs font-bold hover:border-accent transition-colors"
            data-testid="embaucher-renfort"
          >
            + Renfort saisonnier 30 j ({formatMoney(Math.round(CONTRATS_SPECIAUX.renfort_saisonnier.salaire * CHARGES_PATRONALES))}/mois)
          </button>
        </div>
      </div>

      {/* Effectif */}
      <div className="surface-card p-4" data-testid="effectif">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3">Effectif ({employes.length})</h3>
        {employes.length === 0 && <p className="text-xs text-slate-500">Vous gérez seul votre magasin pour l'instant. Le gérant tient la caisse (≈ 100 acheteurs/jour).</p>}
        <div className="space-y-2">
          {employes.map(e => {
            const metier = metiers.find(m => m.id === e.metierId);
            const enFormation = e.enFormationJusqua && e.enFormationJusqua > totalDays;
            return (
              <div key={e.id} className="flex flex-wrap items-center gap-3 text-xs bg-ink-900/50 border border-ink-700 rounded px-3 py-2" data-testid={`emp-${e.id}`}>
                <span className="text-base">{metier?.emoji || '🧑‍💼'}</span>
                <div className="min-w-0">
                  <p className="font-bold text-slate-100">{e.nom}</p>
                  <p className="text-[10px] text-slate-500">
                    {metier?.nom || 'Vendeur'} · {e.contrat === 'alternance' ? 'Alternance' : e.contrat === 'cdd_saisonnier' ? `CDD (fin J${e.finJour})` : 'CDI'}
                    {e.forme && !enFormation && ' · ⭐ Formé (+25 %)'}
                    {enFormation && ` · 🎓 En formation (retour J${e.enFormationJusqua})`}
                  </p>
                </div>
                <span className="ml-auto font-mono text-slate-300">{formatMoney(Math.round(e.salaire * CHARGES_PATRONALES))}/mois</span>
                {!e.forme && !enFormation && e.contrat === 'cdi' && (
                  <button
                    type="button"
                    onClick={() => {
                      const res = formerEmploye(e.id);
                      if (res?.ok) toast('Formation lancée', { description: `${e.nom} revient dans ${FORMATION.duree_jours} jours, +25 % d'efficacité (${formatMoney(FORMATION.cout)}).` });
                      else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante');
                    }}
                    className="flex items-center gap-1 border border-ink-500 text-slate-300 px-2.5 py-1 rounded text-[10px] font-bold hover:border-accent"
                    data-testid={`former-${e.id}`}
                  >
                    <GraduationCap className="w-3 h-3" /> Former ({formatMoney(FORMATION.cout)})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const res = licencierEmploye(e.id);
                    if (res?.ok) toast('Départ acté', { description: `Indemnité versée : ${formatMoney(res.indemnite)}.` });
                  }}
                  className="flex items-center gap-1 border border-red-900/60 text-red-400 px-2.5 py-1 rounded text-[10px] font-bold hover:border-red-500"
                  data-testid={`licencier-${e.id}`}
                >
                  <UserMinus className="w-3 h-3" /> Licencier
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Résumé des effets */}
      <div className="surface-card p-4 text-xs text-slate-400" data-testid="effets-equipe">
        <p className="font-bold text-slate-300 uppercase tracking-wider text-[10px] mb-1.5">Effets actuels de l'équipe</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono">
          <p>Conversion : <span className={effects.conversionBonus >= 0 ? 'text-emerald-400' : 'text-red-400'}>{(effects.conversionBonus * 100).toFixed(0)} %</span></p>
          <p>Trafic : <span className="text-emerald-400">+{(effects.traficBonus * 100).toFixed(0)} %</span></p>
          <p>Capacité caisse : <span className="text-slate-200">{100 + effects.capaciteCaisse} acheteurs/j</span></p>
          <p>Ruptures sauvées : <span className="text-slate-200">{(effects.tauxRecupRupture * 100).toFixed(0)} %</span></p>
        </div>
      </div>
    </div>
  );
}
